// / — 今日工作台(C10,業主 2026-07-09 重設計裁決)。
//
// KPI 來自 GET /v3/admin/stats(失敗顯示「—」,頁面不掛);待辦佇列只用
// **已部署的 LIVE 端點**組 triage(待審開發者申請/待審機型發布/進行中診斷
// 鎖定),各佇列獨立 fan-out、失敗各自降級不拖垮整頁;四域入口卡自 manifest
// 導覽分組生成。舊 workflow 靜態卡牆退役;governance links 移能力覆蓋頁。
//
// 2026-07-10 視覺改版(新設計系統):KPI 由「6 顆同權重」改「1 主 + 4 副」+
// 「服務版本」移出 metric-strip 併入下方系統狀態摘要條;待辦佇列補狀態小圓點
// + 中文空狀態;四域入口由純文字連結清單改 2×2 卡片矩陣(仿 /operations 正面
// 範本);新增第三段系統狀態摘要條填滿下半頁空白,不新增任何 API 呼叫。

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { visibleGroups } from '../../platform/adminNavigation';
import { useAuth } from '../../auth/AuthContext';
import { getAdminStats, getVersionInfo, type AdminStats, type VersionInfo } from '../_shared/adminTelemetryApi';
import { listApplications, listModelReviews } from '../devreview/devReviewApi';
import { listDiagnosticLocks } from '../diagnostics/diagnosticLocksApi';
import { fetchHandoffs, fetchKbGaps } from '../ai-ops/aiOpsApi';
import { roleLabel } from '../users/userModel';
import { SubsystemLauncher } from './SubsystemLauncher';
import '../_shared/shared.css';
import './DashboardPage.css';

interface QueueState {
  key: string;
  label: string;
  detail: string;
  to: string;
  /** null=載入中;'error'=端點不可用(降級顯示 —);number=佇列長度 */
  count: number | 'error' | null;
  /** true=僅在佇列列表顯示,不計入分域 badge(backlog 型信號,如知識缺口長期非零會讓 badge 常亮)。 */
  badgeExempt?: boolean;
}

// 四域固定語意 icon(取代「取中文標籤首字」的程式化 placeholder)
const DOMAIN_ICON: Record<string, string> = {
  客戶支援: '🎧',
  'AI 營運': '🤖',
  產品與生態: '📦',
  維運觀測: '📈',
  信任與治理: '🛡️',
};

const QUEUE_DEFS: Omit<QueueState, 'count'>[] = [
  { key: 'dev-apps', label: '待審開發者申請', detail: '帳號/組織核可', to: '/dev-reviews' },
  { key: 'model-reviews', label: '待審機型發布', detail: '開發者已提交發布申請', to: '/dev-reviews' },
  { key: 'handoffs', label: '轉真人待接', detail: '客服升級單待處理', to: '/ai/handoffs' },
  { key: 'kb-gaps', label: '知識缺口', detail: 'no_answer 待補知識', to: '/ai/kb-gaps', badgeExempt: true },
  { key: 'diag-locks', label: '進行中診斷鎖定', detail: '使用中的範圍日誌鎖定', to: '/diagnostics' },
];

function useTriageQueues(): { queues: QueueState[]; reload: () => void } {
  const [queues, setQueues] = useState<QueueState[]>(QUEUE_DEFS.map((d) => ({ ...d, count: null })));
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    // 每輪(含手動刷新)先回到載入態,讓刷新有回饋、不留上一輪殘值
    setQueues(QUEUE_DEFS.map((d) => ({ ...d, count: null })));
    const set = (key: string, count: number | 'error') =>
      alive && setQueues((qs) => qs.map((q) => (q.key === key ? { ...q, count } : q)));
    listApplications('applied')
      .then((a) => set('dev-apps', a.length))
      .catch(() => set('dev-apps', 'error'));
    listModelReviews()
      .then((m) => set('model-reviews', m.length))
      .catch(() => set('model-reviews', 'error'));
    // AI 營運待辦(R19 起 live):轉真人 open=待接、知識缺口=no_answer 彙集
    fetchHandoffs('open')
      .then((r) => set('handoffs', r.handoffs.length))
      .catch(() => set('handoffs', 'error'));
    fetchKbGaps()
      .then((r) => set('kb-gaps', r.gaps.length))
      .catch(() => set('kb-gaps', 'error'));
    listDiagnosticLocks({ status: 'active' })
      .then((p) => set('diag-locks', p.locks.length))
      .catch(() => set('diag-locks', 'error'));
    return () => {
      alive = false;
    };
  }, [nonce]);
  return { queues, reload: () => setNonce((n) => n + 1) };
}

function useVersion(): VersionInfo | null {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  useEffect(() => {
    let alive = true;
    getVersionInfo()
      .then((r) => alive && setInfo(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return info;
}

function useAdminStats(): { stats: AdminStats | null | 'error'; reload: () => void } {
  const [st, setSt] = useState<AdminStats | null | 'error'>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    if (nonce > 0) setSt(null); // 手動刷新回到載入態(首載已是 null)
    getAdminStats()
      .then((r) => alive && setSt((r.data as AdminStats) ?? 'error'))
      .catch(() => alive && setSt('error'));
    return () => {
      alive = false;
    };
  }, [nonce]);
  return { stats: st, reload: () => setNonce((n) => n + 1) };
}

function StatNum({ st, k }: { st: AdminStats | null | 'error'; k: keyof AdminStats }) {
  if (st === null) return <span className="skeleton-bar dsh-stat-skeleton" aria-hidden="true" />;
  if (st === 'error') return <span className="text-tertiary" title="載入失敗">—</span>;
  return <>{st[k] ?? 0}</>;
}

function MetricCell({
  label,
  value,
  foot,
  to,
  hero,
}: {
  label: string;
  value: ReactNode;
  foot: string;
  to?: string;
  hero?: boolean;
}) {
  const content = (
    <>
      <span className="metric-label">{label}</span>
      <span className={`metric-value${hero ? ' metric-value--hero' : ''}`}>{value}</span>
      <span className="metric-foot">{foot}</span>
    </>
  );
  const cellClass = `metric-cell${hero ? ' metric-cell--hero' : ''}${!to ? ' dsh-readonly' : ''}`;
  if (to) {
    return (
      <Link to={to} className={cellClass}>
        {content}
      </Link>
    );
  }
  return <div className={cellClass}>{content}</div>;
}

// 分域待辦徽章:pending(有待辦)> error(部分佇列失敗)> loading(仍在載入)> null(確定清空)。
// 只認「真的 0」為清空,失敗/載入不得偽裝成無待辦(與佇列面板同一誠實原則)。
type DomainBadge = { kind: 'pending'; n: number } | { kind: 'error' } | { kind: 'loading' } | null;

export default function DashboardPage() {
  const { stats, reload: reloadStats } = useAdminStats();
  const version = useVersion();
  const { queues, reload: reloadQueues } = useTriageQueues();
  const { role, user } = useAuth();
  const windowHours = stats && stats !== 'error' ? stats.window_hours ?? 24 : 24;
  // domain cards mirror the nav exactly (same role filter + C7 orphan rule via
  // platform/adminNavigation.visibleGroups) minus the pinned overview and soon placeholders —
  // sidebar and workbench must never disagree on grouping.
  const domains = visibleGroups(role)
    .filter((g) => g.label !== '')
    .map((g) => ({ label: g.label, items: g.items.filter((i) => !i.soon) }))
    .filter((g) => g.items.length > 0);

  // 各域待辦徽章:佇列 to 命中域內任一項路由即歸屬該域(如轉真人/知識缺口屬 AI 營運域)。
  const domainBadges: DomainBadge[] = domains.map((d) => {
    const routes = new Set(d.items.map((i) => i.to));
    // badgeExempt 佇列(如知識缺口)只出現在列表、完全不驅動 badge(連 error/loading 都不算)
    const relevant = queues.filter((q) => routes.has(q.to) && !q.badgeExempt);
    if (relevant.length === 0) return null; // 該域無任何(計入 badge 的)待辦佇列來源 → 永遠乾淨
    const sum = relevant.reduce((s, q) => (typeof q.count === 'number' ? s + q.count : s), 0);
    if (sum > 0) return { kind: 'pending', n: sum };
    if (relevant.some((q) => q.count === 'error')) return { kind: 'error' };
    if (relevant.some((q) => q.count === null)) return { kind: 'loading' };
    return null; // 全部解析完且為 0 → 確定清空
  });

  const refreshAll = () => {
    reloadStats();
    reloadQueues();
  };
  const queuesRefreshing = queues.some((q) => q.count === null);

  const queuesLoaded = queues.every((q) => q.count !== null);
  // 「全部清空」只認真的 0;三條佇列全部查詢失敗要顯示錯誤,不可偽裝成綠勾 all-clear。
  const queuesAllError = queuesLoaded && queues.every((q) => q.count === 'error');
  const queuesAllEmpty = queuesLoaded && queues.every((q) => q.count === 0);

  const roleText = roleLabel(role ?? undefined);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">總覽</h1>
          <p className="page-subtitle">今日工作台:平台狀態、待辦佇列與分域入口。</p>
        </div>
      </div>

      <div className="dsh-content">
        {/* 指標區:1 主(在線裝置,即時性最貼近工作台)+ 4 副;服務版本移出併入下方系統狀態摘要條 */}
        <div className="metric-strip metric-strip--hero" data-testid="kpi-grid">
          <MetricCell
            hero
            label="在線裝置"
            value={<span data-testid="kpi-online"><StatNum st={stats} k="online_devices" /></span>}
            foot="在線快照"
            to="/devices"
          />
          <MetricCell
            label="使用者"
            value={<span data-testid="kpi-users"><StatNum st={stats} k="users" /></span>}
            foot="客戶總覽"
            to="/users"
          />
          <MetricCell
            label="裝置"
            value={<span data-testid="kpi-devices"><StatNum st={stats} k="devices" /></span>}
            foot="裝置清單"
            to="/devices"
          />
          <MetricCell
            label={`近 ${windowHours} 小時事件`}
            value={<span data-testid="kpi-events"><StatNum st={stats} k="events" /></span>}
            foot="事件紀錄"
          />
          <MetricCell
            label="OTA 任務"
            value={<span data-testid="kpi-ota"><StatNum st={stats} k="ota_jobs" /></span>}
            foot="韌體生命週期"
            to="/firmware"
          />
        </div>

        <div className="dashboard-grid dashboard-grid--stretch">
          <section className="dashboard-panel" data-testid="triage-queues">
            <div className="dashboard-panel-head">
              <h2>待辦佇列</h2>
              <button
                type="button"
                className="btn-ghost btn-sm dsh-refresh-btn"
                onClick={refreshAll}
                disabled={queuesRefreshing}
                data-testid="dashboard-refresh"
                title="重新整理指標與待辦佇列"
              >
                ↻ {queuesRefreshing ? '整理中…' : '重新整理'}
              </button>
            </div>
            {queuesAllError ? (
              <div className="empty-state--inline dsh-queue-empty">
                <div className="empty-state-icon">⚠️</div>
                <div className="empty-state-title">佇列狀態暫時無法取得</div>
                <div className="empty-state-detail">開發者申請、機型發布、轉真人、知識缺口與診斷鎖定的佇列查詢均失敗,請稍後再試。</div>
              </div>
            ) : queuesAllEmpty ? (
              <div className="empty-state--inline dsh-queue-empty">
                <div className="empty-state-icon">✓</div>
                <div className="empty-state-title">目前沒有待處理項目</div>
                <div className="empty-state-detail">佇列已清空,開發者申請、機型發布、轉真人、知識缺口與診斷鎖定都沒有待審項目。</div>
              </div>
            ) : (
              <ul className="dashboard-list">
                {queues.map((q) => {
                  const isPending = q.count !== null && q.count !== 'error' && q.count > 0;
                  return (
                    <li key={q.key}>
                      <span>
                        <span className="dashboard-list-title">
                          {q.label}
                          <span className={`pill pill-dot dsh-queue-pill ${isPending ? 'pill-warn' : 'pill-neutral'}`}>
                            {q.count === null ? '…' : q.count === 'error' ? '—' : q.count}
                          </span>
                        </span>
                        <span className="dashboard-list-meta">{q.detail}</span>
                      </span>
                      <Link className="btn-ghost btn-sm" to={q.to}>
                        開啟
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="dashboard-panel" data-testid="domain-cards">
            <div className="dashboard-panel-head">
              <h2>分域入口</h2>
              <span className="text-tertiary">分域導覽</span>
            </div>
            <div className="dsh-domain-grid">
              {domains.map((d, idx) => {
                const badge = domainBadges[idx];
                return (
                  <Link key={d.label} to={d.items[0].to} className="domain-card">
                    <div className="domain-card-head">
                      <div className="dsh-domain-card-title-row">
                        <span className="domain-card-icon" aria-hidden="true">
                          {DOMAIN_ICON[d.label] ?? '🗂️'}
                        </span>
                        <span className="domain-card-title">{d.label}</span>
                      </div>
                      {badge?.kind === 'pending' && (
                        <span className="pill pill-warn dsh-domain-pill">{badge.n} 待辦</span>
                      )}
                      {badge?.kind === 'error' && (
                        <span className="pill pill-neutral dsh-domain-pill" title="部分待辦佇列暫時無法取得">—</span>
                      )}
                      {badge?.kind === 'loading' && (
                        <span className="pill pill-neutral dsh-domain-pill" aria-hidden="true">…</span>
                      )}
                    </div>
                    <div className="domain-card-foot">
                      <span>{d.items.length} 個頁面</span>
                      <span className="text-brand">進入 →</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>

        {/* 第三段:系統狀態摘要條(復用既有 version info,不新增 API 呼叫),鋪滿寬度解決下半頁留白 */}
        <div className="dsh-status-bar" data-testid="system-status-bar">
          <div className="dsh-status-item">
            <span className="dsh-status-item-label">服務版本</span>
            <span className="dsh-status-item-value">
              <span className={`pill pill-dot ${version ? 'pill-good' : 'pill-neutral'}`}>
                {version ? '已連線' : '載入中'}
              </span>
              <span className="mono">{version?.version ?? '—'}</span>
            </span>
          </div>
          <div className="dsh-status-item">
            <span className="dsh-status-item-label">服務名稱</span>
            <span className="dsh-status-item-value">{version?.service ?? 'restful-api'}</span>
          </div>
          {version?.commit && (
            <div className="dsh-status-item">
              <span className="dsh-status-item-label">Commit</span>
              <span className="dsh-status-item-value mono">{version.commit.slice(0, 8)}</span>
            </div>
          )}
          <div className="dsh-status-spacer" />
          <div className="dsh-status-item">
            <span className="dsh-status-item-label">平台身份</span>
            <span className="dsh-status-item-value">
              {roleText}
              {user?.email ? `·${user.email}` : ''}
            </span>
          </div>
        </div>

        {/* 平台子系統入口(業主 2026-07-12):開發者入口/監控/文件/模擬器…都能從 admin 進入 */}
        <SubsystemLauncher />
      </div>
    </>
  );
}
